import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const PermissionsContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function PermissionsProvider({ children }) {
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const [permissions, setPermissions] = useState({});
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [permissionLevel, setPermissionLevel] = useState(null);
  const fetchedForUser = useRef(null);

  useEffect(() => {
    const fetchPermissions = async () => {
      // Wait for auth to finish loading first
      if (authLoading) {
        return;
      }

      // If not authenticated, reset and stop loading
      if (!isAuthenticated || !user) {
        setPermissions({});
        setIsAdmin(false);
        setLoading(false);
        fetchedForUser.current = null;
        return;
      }

      // Avoid re-fetching for the same user
      if (fetchedForUser.current === user.id) {
        setLoading(false);
        return;
      }

      // If user is admin by role, set isAdmin immediately
      if (user?.role === 'admin') {
        setIsAdmin(true);
      }

      try {
        const response = await axios.get(`${API}/auth/my-permissions`);
        const data = response.data;
        
        setIsAdmin(data.is_admin || user.role === 'admin');
        setPermissions(data.permissions || {});
        setPermissionLevel(data.level_name);
        fetchedForUser.current = user.id;
        
      } catch (error) {
        console.error('Failed to fetch permissions:', error);
        // If admin role, grant all permissions as fallback
        if (user?.role === 'admin') {
          setIsAdmin(true);
          setPermissions({});
        }
        fetchedForUser.current = user.id; // Mark as fetched even on error
      } finally {
        setLoading(false);
      }
    };

    fetchPermissions();
  }, [isAuthenticated, user, authLoading]);

  // Check if user has a specific permission
  const hasPermission = (permissionKey) => {
    // Admins have all permissions
    if (isAdmin || user?.role === 'admin') {
      return true;
    }
    return permissions[permissionKey] === true;
  };

  // Check if user has any of the specified permissions
  const hasAnyPermission = (permissionKeys) => {
    if (isAdmin || user?.role === 'admin') {
      return true;
    }
    return permissionKeys.some(key => permissions[key] === true);
  };

  // Check if user has all of the specified permissions
  const hasAllPermissions = (permissionKeys) => {
    if (isAdmin || user?.role === 'admin') {
      return true;
    }
    return permissionKeys.every(key => permissions[key] === true);
  };

  const value = {
    permissions,
    isAdmin,
    loading,
    permissionLevel,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    refreshPermissions: async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API}/auth/my-permissions`);
        setPermissions(response.data.permissions || {});
        setIsAdmin(response.data.is_admin);
        setPermissionLevel(response.data.level_name);
      } catch (error) {
        console.error('Failed to refresh permissions:', error);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
}

export function usePermissions() {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionsProvider');
  }
  return context;
}

// Higher-order component for permission-based rendering
export function RequirePermission({ permission, permissions, any = false, children, fallback = null }) {
  const { hasPermission, hasAnyPermission, hasAllPermissions, isAdmin, loading } = usePermissions();

  if (loading) {
    return null;
  }

  if (isAdmin) {
    return children;
  }

  if (permission) {
    return hasPermission(permission) ? children : fallback;
  }

  if (permissions) {
    if (any) {
      return hasAnyPermission(permissions) ? children : fallback;
    }
    return hasAllPermissions(permissions) ? children : fallback;
  }

  return children;
}
